import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Skeleton, CircularProgress, Tooltip, Dialog, DialogContent } from '@mui/material';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import { createChart, IChartApi, LineStyle } from 'lightweight-charts';
import { bloomberg } from '../theme/bloombergTheme';
import { TickerLink } from '../components/TickerActionMenu';
import api, { getPatterns, getPatternCounts, ChartPatternRow } from '../services/api';

// ──────────────────────────────────────────────────────────────────────────
// Patterns — showcase stocks with an interesting, exploitable chart pattern.
// Deliberately simple surface: the engine's complexity (tiers, backtested
// probabilities, expectancy) is distilled into one Edge grade + one status
// badge. Default view = actionable setups only (breaking / at trigger /
// confirmed); early-stage forming setups live behind the Forming chip.
// ──────────────────────────────────────────────────────────────────────────

const PATTERN_LABELS: Record<string, string> = {
  double_bottom: 'Double Bottom',
  double_top: 'Double Top',
  inv_head_shoulders: 'Inverse H&S',
  head_shoulders: 'Head & Shoulders',
  ascending_triangle: 'Ascending Triangle',
  descending_triangle: 'Descending Triangle',
  symmetrical_triangle: 'Symmetrical Triangle',
  channel_up: 'Channel Up',
  channel_down: 'Channel Down',
};

// Channels are cut from the page (no breakout semantics / no Edge stats);
// labels retained for rendering safety, dropdown uses PATTERN_OPTIONS.
const PATTERN_OPTIONS = Object.entries(PATTERN_LABELS).filter(([k]) => !k.startsWith('channel'));

const COUNTRY_CHIPS = [
  { value: 'all', label: 'All' },
  { value: 'US', label: 'US' },
  { value: 'UK', label: 'UK' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'JP', label: 'Japan' },
  { value: 'other', label: 'Other' },
];

// Signal states map the engine's tiers onto how a user actually thinks.
const SIGNAL_CHIPS: { value: string; label: string }[] = [
  { value: 'actionable', label: 'Actionable' },   // tiers 0-2 (default)
  { value: 'breaking', label: '🔥 Breaking' },     // tier 0
  { value: 'trigger', label: '⚡ At Trigger' },    // tier 1
  { value: 'confirmed', label: 'Confirmed' },      // tier 2
  { value: 'forming', label: 'Forming' },          // tier 3 (early setups)
  { value: 'all', label: 'All' },
];

const GREEN = '#22c55e';
const RED = '#ef4444';
// lightweight-charts renders to <canvas> and cannot resolve CSS variables like
// the bloomberg.* tokens — chart internals must use literal colors.
const CHART_ORANGE = '#ff8c00';
const CHART_MUTED = '#94a3b8';
const CHART_MUTED_DIM = '#64748b';
const CHART_BORDER = '#2a2e39';

const fmtCap = (v: number | null) => (v == null ? '—' : v >= 1e12 ? `$${(v / 1e12).toFixed(1)}T` : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(0)}M`);

// Edge grade: distills expectancy (R units) into an instant read.
const edgeGrade = (ev: number | null): { g: string; color: string; bg: string } | null => {
  if (ev == null) return null;
  if (ev >= 0.3) return { g: 'A', color: GREEN, bg: 'rgba(34,197,94,0.15)' };
  if (ev >= 0.15) return { g: 'B', color: '#a3e635', bg: 'rgba(163,230,53,0.12)' };
  if (ev >= 0) return { g: 'C', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' };
  return { g: 'D', color: RED, bg: 'rgba(239,68,68,0.13)' };
};

// Lightweight SVG chart rendered purely from the row's STORED series — zero
// network fetches, so sparklines and gallery cards paint instantly.
const MiniChart: React.FC<{ row: ChartPatternRow; height: number; detail?: boolean }> = ({ row, height, detail }) => {
  const series = row.geometry?.series;
  if (!series || series.length < 2) return <span style={{ color: '#64748b', fontSize: 10 }}>—</span>;
  const VW = 300;
  const lineVals = (row.geometry?.lines || []).flatMap(l => l.points.map(pt => pt.price));
  const vals = series.map(x => x.p).concat(lineVals);
  const lo0 = Math.min(...vals), hi0 = Math.max(...vals);
  const pad = (hi0 - lo0) * 0.06 || 1;
  const lo = lo0 - pad, hi = hi0 + pad;
  const X = (i: number) => (i / (series.length - 1)) * VW;
  const Y = (pv: number) => height - ((pv - lo) / (hi - lo)) * height;
  const dates = series.map(x => x.d);
  const xForDate = (d: string) => {
    let i = dates.findIndex(x => x >= d);
    if (i < 0) i = dates.length - 1;
    return X(i);
  };
  const c = row.direction === 'bullish' ? GREEN : RED;
  const path = series.map((x, i) => `${i === 0 ? 'M' : 'L'}${X(i).toFixed(1)},${Y(x.p).toFixed(1)}`).join(' ');
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${VW} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <path d={path} fill="none" stroke={CHART_ORANGE} strokeWidth={detail ? 1.6 : 1.2} vectorEffect="non-scaling-stroke" />
      {(row.geometry?.lines || []).map((ln, li) => {
        const pts = ln.points;
        if (pts.length < 2) return null;
        return (
          <line key={li}
            x1={xForDate(pts[0].date)} y1={Y(pts[0].price)}
            x2={xForDate(pts[pts.length - 1].date)} y2={Y(pts[pts.length - 1].price)}
            stroke={c} strokeWidth={1.1} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" opacity={0.9} />
        );
      })}
      {detail && (row.geometry?.pivots || []).map((pv, pi) => (
        <circle key={pi} cx={xForDate(pv.date)} cy={Y(pv.price)} r={2.4} fill={c} />
      ))}
    </svg>
  );
};

// Inline chart that draws the price series + stored pattern geometry.
const PatternChart: React.FC<{ row: ChartPatternRow }> = ({ row }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let chart: IChartApi | null = null;
    let disposed = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/stock/${encodeURIComponent(row.symbol)}/history?period=1y`);
        if (disposed || !ref.current) return;
        const series = (data || [])
          .filter((d: any) => d.price > 0)
          .map((d: any) => ({ time: d.date, value: d.price }));
        if (!series.length) { setError('No price history available'); setLoading(false); return; }

        chart = createChart(ref.current, {
          height: 320,
          autoSize: true,  // container is sized by the row; canvas must track it
          layout: { background: { color: 'transparent' }, textColor: CHART_MUTED, fontSize: 10 },
          grid: { vertLines: { color: 'rgba(148,163,184,0.07)' }, horzLines: { color: 'rgba(148,163,184,0.07)' } },
          rightPriceScale: { borderColor: CHART_BORDER },
          timeScale: { borderColor: CHART_BORDER },
          crosshair: { horzLine: { labelBackgroundColor: '#333' }, vertLine: { labelBackgroundColor: '#333' } },
        });
        const price = chart.addAreaSeries({
          lineColor: CHART_ORANGE, lineWidth: 2,
          topColor: 'rgba(255,140,0,0.18)', bottomColor: 'rgba(255,140,0,0.0)',
        });
        price.setData(series);

        const bull = row.direction === 'bullish';
        const c = bull ? GREEN : RED;

        (row.geometry?.lines || []).forEach((line) => {
          const pts = line.points
            .map(p => ({ time: p.date, value: p.price }))
            .sort((a, b) => (a.time < b.time ? -1 : 1));
          if (pts.length < 2) return;
          const ls = chart!.addLineSeries({
            color: c, lineWidth: 2, lineStyle: LineStyle.Dashed,
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
          });
          ls.setData(pts);
        });

        const knownTimes = new Set(series.map((d: any) => d.time));
        const markers = (row.geometry?.pivots || [])
          .filter(p => knownTimes.has(p.date))
          .map(p => ({
            time: p.date,
            position: 'inBar' as const,
            color: c, shape: 'circle' as const, size: 1,
          }));
        if (markers.length) price.setMarkers(markers.sort((a, b) => (a.time < b.time ? -1 : 1)) as any);

        price.createPriceLine({ price: row.target_price, color: c, lineStyle: LineStyle.Dotted, lineWidth: 1, title: `target ${row.target_price}` });
        price.createPriceLine({ price: row.neckline_price, color: CHART_MUTED_DIM, lineStyle: LineStyle.Dashed, lineWidth: 1, title: 'breakout' });

        chart.timeScale().fitContent();
        setLoading(false);
      } catch {
        if (!disposed) { setError('Failed to load chart'); setLoading(false); }
      }
    })();
    return () => { disposed = true; if (chart) chart.remove(); };
  }, [row]);

  return (
    <Box sx={{ position: 'relative', px: 1, py: 1 }}>
      {/* Container stays visible at full size — creating the chart inside a
          display:none div makes lightweight-charts size its canvas to 0 width. */}
      <div ref={ref} style={{ width: '100%', height: 320 }} />
      {/* Detail strip under the chart: the numbers we cut from the table */}
      <Box sx={{ display: 'flex', gap: 3, px: 1, pt: 0.5, flexWrap: 'wrap' }}>
        {[['Last', row.last_price?.toLocaleString()],
          ['Breakout', row.neckline_price?.toLocaleString()],
          ['Target', row.target_price?.toLocaleString()],
          ['Formed', row.end_date || '—'],
          ['Confirmed', row.confirmed_date || '—'],
          ['EV', row.expected_r != null ? `${row.expected_r >= 0 ? '+' : ''}${row.expected_r.toFixed(2)}R` : '—'],
          ['Breakout vol', row.breakout_volume_ratio != null ? `${row.breakout_volume_ratio}× avg` : '—'],
        ].map(([k, v]) => (
          <Typography key={k as string} sx={{ fontSize: 10, color: bloomberg.text.muted }}>
            {k}: <span style={{ color: bloomberg.text.secondary, fontFamily: '"IBM Plex Mono", monospace' }}>{v}</span>
          </Typography>
        ))}
      </Box>
      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {error && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>{error}</Typography>
        </Box>
      )}
    </Box>
  );
};

const Patterns: React.FC = () => {
  const [rows, setRows] = useState<ChartPatternRow[]>([]);
  const [counts, setCounts] = useState<Record<string, { forming: number; confirmed: number }>>({});
  const [tierCounts, setTierCounts] = useState<Record<number, number>>({});
  const [view, setView] = useState<'gallery' | 'list'>('gallery');
  const [modalRow, setModalRow] = useState<ChartPatternRow | null>(null);
  const [total, setTotal] = useState(0);
  const [lastScanned, setLastScanned] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pattern, setPattern] = useState<string>('');
  const [signal, setSignal] = useState<string>('actionable');
  const [direction, setDirection] = useState<string>('');
  const [country, setCountry] = useState<string>('all');
  const [timeframe, setTimeframe] = useState<string>('');
  const [confluence, setConfluence] = useState<boolean>(false);
  const [mine, setMine] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  // Breaking-now hero strip (tier 0), independent of the table filters
  const [breaking, setBreaking] = useState<ChartPatternRow[]>([]);
  const [heroSel, setHeroSel] = useState<ChartPatternRow | null>(null);
  // Backtest stats (for the Edge tooltip)
  const [histStats, setHistStats] = useState<any>(null);

  useEffect(() => {
    getPatterns({ tier: 0, limit: 30 })
      .then(r => setBreaking(r.patterns))
      .catch(() => {});
    api.get('/api/patterns/stats').then(r => setHistStats(r.data?.stats || null)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sig: any =
        signal === 'breaking' ? { tier: 0 } :
        signal === 'trigger' ? { tier: 1 } :
        signal === 'confirmed' ? { tier: 2 } :
        signal === 'forming' ? { tier: 3 } :
        signal === 'all' ? {} : { max_tier: 2 };  // 'actionable' default
      const [res, cnts] = await Promise.all([
        getPatterns({ pattern, direction, country, timeframe, ...sig,
          ...(confluence ? { confluence: true } : {}), ...(mine ? { mine: true } : {}),
          min_cap_usd: 5e8, limit: 300 }),
        getPatternCounts(country, 5e8, timeframe),
      ]);
      setRows(res.patterns);
      setTotal(res.total_matched);
      setLastScanned(res.last_scanned);
      setCounts((cnts as any).patterns || {});
      setTierCounts((cnts as any).tiers || {});
    } catch { /* interceptor handles auth */ }
    setLoading(false);
    setExpanded(null);
  }, [pattern, signal, direction, country, timeframe, confluence, mine]);

  useEffect(() => { load(); }, [load]);

  const pill = (active: boolean, label: string, onClick: () => void) => (
    <Box key={label} onClick={onClick} sx={{
      cursor: 'pointer', px: 1, py: '3px', borderRadius: '4px', fontSize: 11, fontWeight: 700,
      whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 0.5,
      bgcolor: active ? bloomberg.accent.orange : bloomberg.bg.tertiary,
      color: active ? bloomberg.text.inverse : bloomberg.text.secondary,
      border: `1px solid ${active ? bloomberg.accent.orange : bloomberg.border.primary}`,
    }}>{label}</Box>
  );

  const th: React.CSSProperties = {
    padding: '5px 8px', fontSize: 10, fontWeight: 700, textAlign: 'left',
    color: bloomberg.accent.orange, textTransform: 'uppercase', letterSpacing: '0.05em',
    borderBottom: `2px solid ${bloomberg.accent.orange}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = {
    padding: '5px 8px', fontSize: 12, borderBottom: `1px solid ${bloomberg.border.primary}`,
    whiteSpace: 'nowrap',
  };

  const countFor = (p: string) => {
    const c = counts[p]; return c ? c.forming + c.confirmed : 0;
  };
  const t = (n: number) => tierCounts[n] || 0;
  const signalCount = (v: string) =>
    v === 'breaking' ? t(0) : v === 'trigger' ? t(1) : v === 'confirmed' ? t(2)
    : v === 'forming' ? t(3) : v === 'all' ? t(0) + t(1) + t(2) + t(3) : t(0) + t(1) + t(2);

  // ── Column sorting (client-side; ISO dates sort lexically = chronologically)
  const [sortCol, setSortCol] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortCol(col);
      setSortDir(['symbol', 'name', 'pattern', 'status'].includes(col) ? 'asc' : 'desc');
    }
  };

  const sortVal = (r: ChartPatternRow, col: string): string | number => {
    switch (col) {
      case 'symbol': return r.symbol;
      case 'name': return r.name || '';
      case 'pattern': return PATTERN_LABELS[r.pattern_type] || r.pattern_type;
      case 'status': return r.tier;
      case 'target': return r.target_price ?? -Infinity;
      case 'upside': return r.last_price > 0 ? (r.target_price - r.last_price) / r.last_price : -Infinity;
      case 'edge': return r.expected_r ?? -Infinity;
      case 'where': return r.market_cap_usd ?? -Infinity;
      default: return 0;
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortCol) return rows;  // server order: tier → expectancy → confluence → recency
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortVal(a, sortCol), vb = sortVal(b, sortCol);
      if (typeof va === 'string' || typeof vb === 'string') {
        return String(va).localeCompare(String(vb)) * dir;
      }
      return ((va as number) - (vb as number)) * dir;
    });
  }, [rows, sortCol, sortDir]);

  const sTh = (label: string, col: string, right?: boolean) => (
    <th
      style={{ ...th, textAlign: right ? 'right' : 'left', cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(col)}
      title={`Sort by ${label}`}
    >
      {label}{sortCol === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
    </th>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2 }}>
        <CandlestickChartIcon sx={{ color: bloomberg.accent.orange, alignSelf: 'center' }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Chart Patterns</Typography>
        <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>
          {loading ? 'loading…' : `${total} setups · $500M+ caps`}
          {lastScanned && ` · scanned ${new Date(lastScanned + 'Z').toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`}
        </Typography>
      </Box>

      {/* 🔥 Breaking-now hero strip — click a chip to preview its chart */}
      {breaking.length > 0 && (
        <Paper elevation={2} sx={{ p: 1.25, mb: 2, border: '1px solid rgba(255,140,0,0.35)', bgcolor: 'rgba(255,140,0,0.05)' }}>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: bloomberg.accent.orange, mr: 0.5 }}>
              🔥 BREAKING NOW ({breaking.length})
            </Typography>
            {breaking.slice(0, 14).map(b => (
              <Box key={b.id} onClick={() => setHeroSel(heroSel?.id === b.id ? null : b)} sx={{
                cursor: 'pointer', px: 1, py: '3px', borderRadius: '4px', fontSize: 11, fontWeight: 700,
                display: 'inline-flex', gap: 0.5, alignItems: 'center', whiteSpace: 'nowrap',
                bgcolor: heroSel?.id === b.id ? bloomberg.accent.orange : bloomberg.bg.tertiary,
                color: heroSel?.id === b.id ? bloomberg.text.inverse : (b.direction === 'bullish' ? GREEN : RED),
                border: `1px solid ${heroSel?.id === b.id ? bloomberg.accent.orange : bloomberg.border.primary}`,
              }}>
                {b.confluence ? '★ ' : ''}{b.symbol}
                <span style={{ fontWeight: 400, opacity: 0.85 }}>{PATTERN_LABELS[b.pattern_type] || b.pattern_type}</span>
              </Box>
            ))}
            {breaking.length > 14 && (
              <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>+{breaking.length - 14} more in table</Typography>
            )}
          </Box>
          {heroSel && <PatternChart row={heroSel} />}
        </Paper>
      )}

      {/* Filters — one row of signal states, one row of context */}
      <Paper elevation={2} sx={{ p: 1.25, mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', mr: 0.5, minWidth: 44 }}>Signal</Typography>
          {SIGNAL_CHIPS.map(c => pill(signal === c.value, `${c.label} ${signalCount(c.value)}`, () => setSignal(c.value)))}
          <Box sx={{ width: 12 }} />
          <Tooltip title="Only patterns on your watched names (stars, model portfolios, tracker)">
            <span>{pill(mine, '★ Mine', () => setMine(!mine))}</span>
          </Tooltip>
          {pill(direction === 'bullish', 'Bullish', () => setDirection(direction === 'bullish' ? '' : 'bullish'))}
          {pill(direction === 'bearish', 'Bearish', () => setDirection(direction === 'bearish' ? '' : 'bearish'))}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: bloomberg.text.muted, textTransform: 'uppercase', mr: 0.5, minWidth: 44 }}>Scope</Typography>
          <select
            value={pattern}
            onChange={e => setPattern(e.target.value)}
            style={{
              background: 'transparent', border: `1px solid ${bloomberg.border.primary}`, borderRadius: 4,
              color: bloomberg.accent.orange, fontSize: 11, fontWeight: 700, cursor: 'pointer',
              outline: 'none', padding: '3px 6px', fontFamily: 'inherit',
            }}
          >
            <option value="" style={{ background: '#1a1a2e', color: '#fff' }}>All patterns</option>
            {PATTERN_OPTIONS.map(([k, v]) => (
              <option key={k} value={k} style={{ background: '#1a1a2e', color: '#fff' }}>
                {v} ({countFor(k)})
              </option>
            ))}
          </select>
          {pill(timeframe === '', 'All horizons', () => setTimeframe(''))}
          {pill(timeframe === 'short', '6M', () => setTimeframe('short'))}
          {pill(timeframe === 'long', '1Y–2Y', () => setTimeframe('long'))}
          <Tooltip title="Short & long timeframes agree on direction">
            <span>{pill(confluence, '★ Confluence', () => setConfluence(!confluence))}</span>
          </Tooltip>
          <Box sx={{ width: 12 }} />
          {COUNTRY_CHIPS.map(c => pill(country === c.value, c.label, () => setCountry(c.value)))}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
            {pill(view === 'gallery', '▦ Gallery', () => setView('gallery'))}
            {pill(view === 'list', '☰ List', () => setView('list'))}
          </Box>
        </Box>
      </Paper>

      {/* Results table — 8 columns; the detail lives in the expanded chart */}
      <Paper elevation={2} sx={{ p: 0, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={28} />)}
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 3, fontSize: 13, color: bloomberg.text.muted }}>
            No setups match these filters. The scanner runs nightly after the price sync.
          </Typography>
        ) : view === 'gallery' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 1.5, p: 1.5 }}>
            {sortedRows.map((r) => {
              const c = r.direction === 'bullish' ? GREEN : RED;
              const upside = r.last_price > 0 ? ((r.target_price - r.last_price) / r.last_price) * 100 : 0;
              const grade = edgeGrade(r.expected_r);
              return (
                <Paper key={r.id} elevation={1} onClick={() => setModalRow(r)} sx={{
                  p: 1, cursor: 'pointer', border: `1px solid ${bloomberg.border.primary}`,
                  opacity: r.expected_r != null && r.expected_r < 0 ? 0.55 : 1,
                  '&:hover': { borderColor: bloomberg.accent.orange },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    {r.confluence && <span style={{ color: '#ff8c00', fontSize: 12 }}>★</span>}
                    <Box onClick={e => e.stopPropagation()}><TickerLink symbol={r.symbol} fontSize={13} /></Box>
                    <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{r.name}</Typography>
                    {grade && <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 3, background: grade.bg, color: grade.color }}>{grade.g}</span>}
                  </Box>
                  <MiniChart row={r} height={130} detail />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: c }}>{PATTERN_LABELS[r.pattern_type] || r.pattern_type}</Typography>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3,
                      background: r.tier === 0 ? 'rgba(239,68,68,0.18)' : r.tier === 1 ? 'rgba(250,204,21,0.15)' : 'rgba(148,163,184,0.12)',
                      color: r.tier === 0 ? '#f87171' : r.tier === 1 ? '#facc15' : bloomberg.text.secondary }}>
                      {r.tier === 0 ? '🔥' : r.tier === 1 ? '⚡' : r.status}
                    </span>
                    {r.volume_confirmed && <span style={{ fontSize: 9, fontWeight: 700, color: '#22c55e' }}>VOL✓</span>}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: upside >= 0 ? GREEN : RED }}>
                        {upside >= 0 ? '+' : ''}{upside.toFixed(1)}%
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, fontFamily: '"IBM Plex Mono", monospace' }}>{r.country} · {fmtCap(r.market_cap_usd)}</Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {sTh('Symbol', 'symbol')}
                  {sTh('Company', 'name')}
                  {sTh('Pattern', 'pattern')}
                  <th style={{ ...th, width: 120 }}>Chart</th>
                  {sTh('Signal', 'status')}
                  {sTh('Upside', 'upside', true)}
                  {sTh('Edge', 'edge', true)}
                  {sTh('Where', 'where', true)}
                </tr>
              </thead>
              {/* key forces a DOM remount when sort changes so rows actually reorder */}
              <tbody key={`${sortCol}-${sortDir}`}>
                {sortedRows.map((r) => {
                  const bull = r.direction === 'bullish';
                  const c = bull ? GREEN : RED;
                  const upside = r.last_price > 0 ? ((r.target_price - r.last_price) / r.last_price) * 100 : 0;
                  const isOpen = expanded === r.id;
                  const grade = edgeGrade(r.expected_r);
                  const st = histStats?.[r.pattern_type]?.[r.timeframe];
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                        style={{ cursor: 'pointer', backgroundColor: isOpen ? bloomberg.bg.hover : 'transparent',
                          opacity: r.expected_r != null && r.expected_r < 0 ? 0.55 : 1 }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = bloomberg.bg.hover)}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = isOpen ? bloomberg.bg.hover : 'transparent')}
                      >
                        <td style={td} onClick={e => e.stopPropagation()}>
                          {r.confluence && <Tooltip title="Confluence: short & long timeframes agree"><span style={{ color: '#ff8c00', marginRight: 4 }}>★</span></Tooltip>}
                          <TickerLink symbol={r.symbol} fontSize={12} />
                        </td>
                        <td style={{ ...td, color: bloomberg.text.secondary, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name || '—'}</td>
                        <td style={{ ...td, fontWeight: 700, color: c }}>
                          {PATTERN_LABELS[r.pattern_type] || r.pattern_type}
                          <span style={{ fontSize: 9, fontWeight: 700, marginLeft: 6, padding: '1px 4px', borderRadius: 3,
                            background: r.timeframe === 'long' ? 'rgba(96,165,250,0.15)' : 'rgba(148,163,184,0.10)',
                            color: r.timeframe === 'long' ? '#60a5fa' : '#7c8592' }}>
                            {r.timeframe === 'long' ? '1Y–2Y' : '6M'}
                          </span>
                        </td>
                        <td style={{ ...td, width: 120, padding: '2px 8px' }}>
                          <MiniChart row={r} height={30} />
                        </td>
                        <td style={td}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                            padding: '1px 6px', borderRadius: 3,
                            background: r.tier === 0 ? 'rgba(239,68,68,0.18)'
                              : r.tier === 1 ? 'rgba(250,204,21,0.15)'
                              : r.status === 'confirmed' ? 'rgba(255,140,0,0.15)' : 'rgba(148,163,184,0.12)',
                            color: r.tier === 0 ? '#f87171'
                              : r.tier === 1 ? '#facc15'
                              : r.status === 'confirmed' ? bloomberg.accent.orange : bloomberg.text.secondary,
                          }}>
                            {r.tier === 0 ? `🔥 breaking${r.confirmed_date ? ` ${r.confirmed_date.slice(5)}` : ''}`
                              : r.tier === 1 ? '⚡ at trigger'
                              : <>{r.status}{r.status === 'confirmed' && r.confirmed_date ? ` ${r.confirmed_date.slice(5)}` : ''}</>}
                          </span>
                          {r.volume_confirmed && (
                            <Tooltip title={`Breakout volume ${r.breakout_volume_ratio}× the 20-day average`}>
                              <span style={{ fontSize: 9, fontWeight: 700, marginLeft: 4, padding: '1px 4px', borderRadius: 3,
                                background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>VOL✓</span>
                            </Tooltip>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700, color: upside >= 0 ? GREEN : RED }}>
                          {upside >= 0 ? '+' : ''}{upside.toFixed(1)}%
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {grade ? (
                            <Tooltip title={
                              `Edge ${grade.g} (${r.expected_r! >= 0 ? '+' : ''}${r.expected_r!.toFixed(2)}R expected per trade)` +
                              (st?.hit_rate != null ? ` — historically ${(st.hit_rate * 100).toFixed(0)}% of ${st.n} ${PATTERN_LABELS[r.pattern_type]} breakouts hit their target (classical pattern-low stop)` : '') +
                              `. Blends backtested probabilities with this setup's own reward:risk.`
                            }>
                              <span style={{ fontSize: 11, fontWeight: 800, padding: '1px 8px', borderRadius: 3, background: grade.bg, color: grade.color }}>
                                {grade.g}
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="No reliable backtest statistics for this pattern/timeframe yet">
                              <span style={{ fontSize: 11, color: '#64748b' }}>—</span>
                            </Tooltip>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontSize: 11, color: bloomberg.text.muted, fontFamily: '"IBM Plex Mono", monospace' }}>
                          {r.country || '—'} · {fmtCap(r.market_cap_usd)}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, borderBottom: `1px solid ${bloomberg.border.primary}`, background: bloomberg.bg.secondary }}>
                            <PatternChart row={r} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>

      {/* Card detail modal — full interactive chart + the numbers */}
      <Dialog open={!!modalRow} onClose={() => setModalRow(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: bloomberg.bg.secondary, backgroundImage: 'none' } }}>
        <DialogContent sx={{ p: 1.5 }}>
          {modalRow && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {modalRow.confluence && <span style={{ color: '#ff8c00' }}>★</span>}
                <TickerLink symbol={modalRow.symbol} fontSize={15} />
                <Typography sx={{ fontSize: 13, color: bloomberg.text.secondary }}>{modalRow.name}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: modalRow.direction === 'bullish' ? GREEN : RED }}>
                  {PATTERN_LABELS[modalRow.pattern_type] || modalRow.pattern_type}
                </Typography>
              </Box>
              <PatternChart row={modalRow} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Patterns;
